import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { EmailConversationApiService } from '../services/email-conversation-api.service';
import {
  attachmentDeleteFailure,
  attachmentDeleteSuccess,
  attachmentUploadFailure,
  attachmentUploadSuccess,
  copyAttachment,
  deleteAttachment,
  emailMessageLoaded,
  loadEmailMessage,
  sendEmailMessage,
  sendEmailMessageFailure,
  sendEmailMessageSuccess,
  updateEmailDraft,
  uploadAttachment
} from '../actions/email-message.actions';
import { EmailConversation } from '../models/email-conversation.model';
import { from, merge, of } from 'rxjs';
import { Dictionary } from '@ngrx/entity';
import { EmailMessage } from '../models/email-message.model';
import { emailConversationChanged, emailConversationsLoaded } from '../actions/email-conversation.actions';
import { selectEmailMessageEntitiesByConversationId } from '../selectors/email-message.selectors';
import { some } from 'lodash';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

@Injectable()
export class GenesysCloudEmailMessageEffects {
  private readonly DRAFT_ID = 'draft';

  private readonly initialConversations$ = this.actions$.pipe(
    ofType(emailConversationsLoaded),
    mergeMap(({ conversations }) => from(conversations))
  );
  private readonly changedConversations$ = this.actions$.pipe(
    ofType(emailConversationChanged),
    map(({ conversation }) => conversation)
  );

  readonly startLoadNewEmailMessage$ = createEffect(() =>
    merge(this.initialConversations$, this.changedConversations$).pipe(
      concatLatestFrom((conversation) =>
        this.store.select(selectEmailMessageEntitiesByConversationId(conversation.id))
      ),
      filter(
        ([conversation, loadedEmailMessages]) =>
          !this.isLastEmailMessageAlreadyLoaded(conversation, loadedEmailMessages)
      ),
      filter(([conversation]) => !!conversation.lastEmailMessageId),
      map(([conversation, _]) =>
        loadEmailMessage({ conversationId: conversation.id, messageId: conversation.lastEmailMessageId })
      )
    )
  );

  readonly loadNewEmailMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEmailMessage),
      mergeMap(({ conversationId, messageId }) =>
        this.emailApi
          .getEmailMessage$(conversationId, messageId)
          .pipe(map((emailMessage) => emailMessageLoaded({ conversationId, emailMessage })))
      )
    )
  );

  readonly sendEmailMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendEmailMessage),
      mergeMap((action) =>
        this.emailApi.sendEmailMessage$(action.conversationId, action.emailMessage).pipe(
          map((emailMessage) => sendEmailMessageSuccess({ conversationId: action.conversationId, emailMessage })),
          catchError((error: any) =>
            of(
              sendEmailMessageFailure({
                conversationId: action.conversationId,
                errorMessage: this._getErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  readonly uploadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadAttachment),
      mergeMap(({ conversationId, form }) =>
        this.emailApi.uploadAttachment$(conversationId, form).pipe(
          map(() => attachmentUploadSuccess({ conversationId })),
          catchError(() => of(attachmentUploadFailure({ conversationId })))
        )
      )
    )
  );

  readonly deleteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteAttachment),
      mergeMap(({ conversationId, attachmentMeta }) =>
        this.emailApi.deleteAttachment$(conversationId, attachmentMeta.attachmentId).pipe(
          map(() => attachmentDeleteSuccess({ conversationId })),
          catchError(() => of(attachmentDeleteFailure({ conversationId })))
        )
      )
    )
  );

  readonly loadDraft$ = createEffect(() =>
    merge(this.initialConversations$, this.changedConversations$).pipe(
      filter((conversation) =>
        some(conversation.agentParticipant.emails, (e) => e.state === ConnectionState.Connected)
      ),
      mergeMap((conversation) =>
        this.emailApi
          .getEmailMessage$(conversation.id, this.DRAFT_ID)
          .pipe(map((draft) => emailMessageLoaded({ conversationId: conversation.id, emailMessage: draft })))
      )
    )
  );

  readonly updateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEmailDraft),
      mergeMap(({ conversationId, draftMessage }) =>
        this.emailApi.updateEmailDraft$(conversationId, draftMessage).pipe(
          map((draft) =>
            emailMessageLoaded({
              conversationId,
              emailMessage: {
                ...draft,
                htmlBody: draft.htmlBody || '',
                textBody: draft.textBody || ''
              }
            })
          )
        )
      )
    )
  );

  readonly copyAttachment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(copyAttachment),
        mergeMap(({ conversationId, sourceMessageId, attachments }) =>
          this.emailApi.copyAttachmentToDraft$(conversationId, sourceMessageId, attachments)
        )
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly emailApi: EmailConversationApiService
  ) {}

  private isLastEmailMessageAlreadyLoaded(
    conversation: EmailConversation,
    loadedEmailMessages: Dictionary<EmailMessage>
  ): boolean {
    return !!loadedEmailMessages[conversation.lastEmailMessageId];
  }

  private _getErrorMessage(error: Error): string {
    if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.BadRequest) {
      return error.error.message;
    } else {
      return '';
    }
  }
}
